// START IMPORTS
// Vendor imports
import googleTagManager, {
  GoogleTagManagerConfig,
} from '@analytics/google-tag-manager';
import { initialize } from '@ltv-apm-modules/react';
// Core lib imports
import { version, CookiesStorage } from '@ltvco/refresh-lib';
// Context imports
import { SubscriptionAccessConfigProvider } from '@ltvco/refresh-lib/ctx';
// Payments imports
import { TosModalLegalError } from '@ltvco/refresh-lib/payments';
// Theme and UI imports
import { ErrorBoundary, styled } from '@ltvco/refresh-lib/theme';
// Utility imports
import { ErrorRequestData, ResponseError } from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  SearchData,
  invalidateSession,
  useSession,
  SessionProvider,
  isUserLoggedIn,
  ResetPasswordRequestForm,
  MagicLinkModal,
  ForgotLoginModal,
  closeSnackbar,
  CancelFinalStepModal,
  ConfirmCancelModal,
  ImportList,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  SearchFormProvider,
  Footer,
  BannerMigrationCta,
  NonCompliancePage,
  ComplianceRecomplyPage,
  ComplianceProhibitedPage,
  ComplianceFormPage,
  CompliancePermittedPage,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import Analytics from 'analytics';
import { useEffect, useRef, useState } from 'react';
import {
  useNavigate,
  Navigate,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';
// App-specific imports
import 'App.css';

import { AppContainer } from 'components/AppContainer';
import { ContactOptionsModalParent } from 'components/ContactOptions/ContactOptionsModalParent';
import { EmailVerification } from 'components/EmailVerification';
import { Growthbook } from 'components/Growthbook';
import { HeaderParent } from 'components/HeaderParent/HeaderParent';
import { LibContextWrapper } from 'components/LibContextWrapper';
import QueryWrapper from 'components/queryWrapper/QueryWrapper';
import { Recycling } from 'components/recycling/Recycling';
import {
  DashboardParent,
  DashboardPage,
  Fraud,
  Login,
  Report,
  AuthenticatedRoute,
  DarkWebScan,
  Billing,
  Lists,
  ListsItem,
  SearchPersonResultsContainer,
  FeatureGatedRoute,
  SearchListingResults,
  ResetPassword,
  Reactivation,
  PublicRoute,
  NotFound,
  ErrorPage,
  UpgradePlanPage,
  ReportVelocityCaptcha,
  Monitoring,
  NeighborhoodReportSection,
  OnboardingPage,
  RewardsPage,
  RecentReports,
} from 'routes';
import { OwnerSearchParent } from 'routes/Dashboard/OwnerSearch/OwnerSearchParent';
import { generateFooterLinks } from 'utils/footerLinks/footerLinks';
import { goToLogin } from 'utils/goToLogin';

import packageInfo from '../package.json';
// END IMPORTS

// START INTERFACES_AND_TYPES
declare global {
  interface Window {
    env: Record<string, string>;
  }
}
// END INTERFACES_AND_TYPES

// START CONSTANTS
let otel: any = null;

if (
  import.meta.env.VITE_TARGET_ENV === 'production' ||
  import.meta.env.VITE_TARGET_ENV === 'staging'
) {
  otel = initialize({
    name: 'refresh-neighborwho',
    url: import.meta.env.VITE_APM_URL,
    apikey: import.meta.env.VITE_APM_KEY,
  });
}

const isUpgradeFormVariation = JSON.parse(
  localStorage.getItem('isUpgradeFormVariation') || 'false'
);
// END CONSTANTS

// START STYLED_COMPONENTS
const HeaderContainer = styled('div')`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 20;
`;

const MigrationBannerContainer = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const LibVersion = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} 0 0 0`,
  fontSize: theme.typography.fontSize * 0.75,
}));
// END STYLED_COMPONENTS

function App() {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useSession();
  const [isCoreLoginRedirect] = useQueryParam(
    'is_core_login_redirect',
    StringParam
  );
  const [loginType] = useQueryParam('login_type', StringParam);
  const loginPageContainerRef = useRef(null);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [isAuthenticated, setIsAuthenticated] = useState(isUserLoggedIn());
  const [isContactOptionsModalOpen, setIsContactOptionsModalOpen] =
    useState(false);
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] =
    useState(false);
  const [isCancelFinalStepModalOpen, setIsCancelFinalStepModalOpen] =
    useState(false);
  const [isInSingleReportVariation, setIsInSingleReportVariation] =
    useState(false);
  const [tosState, setTosState] = useState<{
    modalOpen: boolean;
    requestData: ErrorRequestData['body'] | null;
  }>({
    modalOpen: false,
    requestData: null,
  });
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const analytics = Analytics({
    app: 'refresh-neighborwho',
    debug: true,
    plugins: [
      googleTagManager({
        containerId: 'GTM-KXN8TR',
        dataLayer: (window as any)?.dataLayer ?? [],
      } as GoogleTagManagerConfig),
    ],
  });
  const storage = new CookiesStorage();
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    if (
      isCoreLoginRedirect === 'true' &&
      !sessionStorage.getItem('has_tracked_core_login_redirect')
    ) {
      analytics.track('site_event', {
        event_name: 'login_attempt',
        type: loginType || 'web',
        success: true,
      });

      if (loginType?.includes('sso')) {
        analytics.track('site_event', {
          event_name: 'sso_success',
          type: true,
        });
      }

      sessionStorage.setItem(
        'has_tracked_core_login_redirect',
        isCoreLoginRedirect
      );
    }
  }, [isCoreLoginRedirect, loginType, analytics]);
  // END EFFECTS

  // START EVENT_HANDLERS
  const changeIsInSingleReportVariation = (variation: boolean) => {
    setIsInSingleReportVariation(variation);
  };

  async function handleLogoutClick(ga4Ttracker?: () => void) {
    await invalidateSession();
    setIsAuthenticated(isUserLoggedIn());
    closeSnackbar();
    storage.setValue('partialLocView', 'false');
    goToLogin('https://www.neighborwho.com');
    if (ga4Ttracker) {
      ga4Ttracker();
    }
  }

  function isResponseError(error: any): error is ResponseError {
    return error.response !== undefined;
  }

  function logError(
    context: string,
    error: Error | ResponseError,
    url: string | undefined
  ) {
    const isProdEnv = import.meta.env.VITE_TARGET_ENV === 'production';
    if (!isProdEnv || !otel) {
      return;
    }

    const { account } = session;
    const { api } = otel;
    if (api.isOTELInitialized()) {
      if (account) {
        api.setUser({
          id: account?.account.user_info.user_code,
          email: account?.account.user_info.email,
        });
      }
      let errorMsg = `${context}: ${error.message}, Cause: ${error.cause}`;
      if (isResponseError(error)) {
        errorMsg = `${errorMsg}, Status: ${
          error.response.status
        }, Data: ${JSON.stringify(error.response)}`;
      }
      if (url) {
        errorMsg += `, URL: ${url}`;
      }
      api.pushError(new Error(errorMsg));
    }
  }

  function trackEventGA4(eventToTrack: object, eventName?: string) {
    const targetEnv = import.meta.env.VITE_TARGET_ENV;
    if (targetEnv === 'dev') {
      return;
    }
    analytics?.track(eventName || 'site_event', eventToTrack);
  }

  function trackEvent(category: string, action: string, label?: string) {
    analytics.track('ua_event', {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
    });
  }

  const onLoginSuccess = () => {
    let next = '/';

    if (location.state && location.state.next) {
      next = location.state.next;
    }

    navigate(
      next === '/' ||
        (next.startsWith('/report') && !next.includes('permalink'))
        ? '/dashboard'
        : next
    );
    setIsAuthenticated(isUserLoggedIn());
  };

  const contactUsHandler = () => {
    setIsContactOptionsModalOpen(true);
  };

  const handleSearchSuccess = ({
    reportType,
    searchParams,
    permalink,
  }: SearchData) => {
    const url = `/report/${reportType}?${searchParams}&permalink=${permalink}`;
    navigate(url);
  };

  const onCancelConfirmCancelModal = () => {
    setIsConfirmCancelModalOpen(false);
  };
  // END EVENT_HANDLERS

  // START RENDER
  return (
    <ErrorBoundary
      redirectFunction={() => {
        navigate('/error');
      }}
      location={location}
      logFunction={logError as any}
      trackEventFunction={trackEventGA4}
    >
      <QueryWrapper
        handleLogout={handleLogoutClick}
        trackEventGA4={trackEventGA4}
        setTosState={setTosState}
      >
        <SessionProvider isAuthenticated={isAuthenticated}>
          <SubscriptionAccessConfigProvider>
            <Growthbook>
              <LibContextWrapper
                trackEventGA4={trackEventGA4}
                logError={logError}
                trackEvent={trackEvent}
                isInSingleReportVariation={isInSingleReportVariation}
                handleLogoutClick={handleLogoutClick}
              >
                <EmailVerification>
                  <SearchFormProvider>
                    <div className="app">
                      <HeaderContainer>
                        <MigrationBannerContainer>
                          <BannerMigrationCta />
                        </MigrationBannerContainer>
                        <HeaderParent
                          handleSearchSuccess={handleSearchSuccess}
                          changeSingleReportVariation={
                            changeIsInSingleReportVariation
                          }
                        />
                      </HeaderContainer>
                      <AppContainer>
                        <Routes>
                          <Route element={<NotFound />} path={'*'} />
                          <Route element={<ErrorPage />} path={'/error'} />
                          <Route
                            index
                            element={<Navigate to={`/dashboard`} />}
                          />
                          <Route
                            path={`/login`}
                            element={
                              <PublicRoute>
                                <Login
                                  onLoginSuccess={onLoginSuccess}
                                  ref={loginPageContainerRef}
                                />
                              </PublicRoute>
                            }
                          >
                            <Route
                              path="reset-password-request"
                              element={
                                <ResetPasswordRequestForm
                                  containerRef={loginPageContainerRef}
                                  open={false}
                                />
                              }
                            />
                            <Route
                              path="magic-link"
                              element={
                                <MagicLinkModal
                                  containerRef={loginPageContainerRef}
                                  open={false}
                                />
                              }
                            />
                            <Route
                              path="forgot-email"
                              element={
                                <ForgotLoginModal
                                  containerRef={loginPageContainerRef}
                                  open={false}
                                />
                              }
                            />
                          </Route>
                          <Route
                            path={`/reset-password`}
                            element={
                              <PublicRoute>
                                <ResetPassword />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path={`dashboard`}
                            element={
                              <AuthenticatedRoute>
                                <DashboardParent
                                  contactUsHandler={contactUsHandler}
                                  handleLogout={handleLogoutClick}
                                />
                              </AuthenticatedRoute>
                            }
                          >
                            <Route
                              index
                              element={
                                <AuthenticatedRoute>
                                  <DashboardPage />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/import-list`}
                              element={
                                <AuthenticatedRoute>
                                  <ImportList />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/reports`}
                              element={
                                <AuthenticatedRoute>
                                  <RecentReports />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/rewards`}
                              element={
                                <AuthenticatedRoute>
                                  <RewardsPage />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/dark-web`}
                              element={
                                <AuthenticatedRoute>
                                  <DarkWebScan />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/lists`}
                              element={
                                <AuthenticatedRoute>
                                  <Lists />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/lists/:listName`}
                              element={
                                <AuthenticatedRoute>
                                  <ListsItem />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/monitoring`}
                              element={
                                <AuthenticatedRoute>
                                  <Monitoring />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={'neighborhood-report'}
                              element={
                                <AuthenticatedRoute>
                                  <NeighborhoodReportSection />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`billing`}
                              element={
                                <AuthenticatedRoute>
                                  <Billing
                                    onCancelConfirmCancelModal={
                                      onCancelConfirmCancelModal
                                    }
                                  />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path="fraud"
                              element={
                                <AuthenticatedRoute>
                                  <Fraud
                                    onSearchSuccess={handleSearchSuccess}
                                  />
                                </AuthenticatedRoute>
                              }
                            />
                          </Route>
                          <Route
                            path={`/report/:reportType`}
                            element={
                              <AuthenticatedRoute>
                                <Report onSearchSuccess={handleSearchSuccess} />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/person`}
                            element={
                              <AuthenticatedRoute>
                                <SearchPersonResultsContainer />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/v2`}
                            element={
                              <AuthenticatedRoute>
                                <FeatureGatedRoute featureKey="person">
                                  <SearchPersonResultsContainer />
                                </FeatureGatedRoute>
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/listing`}
                            element={
                              <AuthenticatedRoute>
                                <SearchListingResults />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/owner`}
                            element={
                              <AuthenticatedRoute>
                                <OwnerSearchParent />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/noncompliance`}
                            element={
                              <AuthenticatedRoute>
                                <NonCompliancePage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/compliance-form`}
                            element={
                              <AuthenticatedRoute>
                                <ComplianceFormPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/compliance-permitted`}
                            element={
                              <AuthenticatedRoute>
                                <CompliancePermittedPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/compliance-prohibited`}
                            element={
                              <AuthenticatedRoute>
                                <ComplianceProhibitedPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/compliance-recomply`}
                            element={
                              <AuthenticatedRoute>
                                <ComplianceRecomplyPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={'/reactivation'}
                            element={
                              <AuthenticatedRoute>
                                <Reactivation />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            index={true}
                            path={'/upgrade/plan'}
                            element={
                              <AuthenticatedRoute>
                                <UpgradePlanPage
                                  contactUsHandler={contactUsHandler}
                                />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={'/captcha'}
                            element={
                              <AuthenticatedRoute>
                                <ReportVelocityCaptcha />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/onboarding`}
                            element={
                              <AuthenticatedRoute>
                                <OnboardingPage />
                              </AuthenticatedRoute>
                            }
                          />
                        </Routes>
                        <Recycling isAuthenticated={isAuthenticated} />
                      </AppContainer>
                      <ContactOptionsModalParent
                        isContactOptionsModalOpen={isContactOptionsModalOpen}
                        setIsContactOptionsModalOpen={() =>
                          setIsContactOptionsModalOpen(
                            !isContactOptionsModalOpen
                          )
                        }
                        openConfirmCancelModal={() =>
                          setIsConfirmCancelModalOpen(true)
                        }
                      />
                      <ConfirmCancelModal
                        isOpen={isConfirmCancelModalOpen}
                        onClose={() => setIsConfirmCancelModalOpen(false)}
                        onCancel={() => {
                          setIsConfirmCancelModalOpen(false);
                          setIsCancelFinalStepModalOpen(true);
                        }}
                      />
                      <CancelFinalStepModal
                        isOpen={isCancelFinalStepModalOpen}
                        onClose={() => setIsCancelFinalStepModalOpen(false)}
                      />
                      <TosModalLegalError
                        tosState={tosState}
                        setTosState={setTosState}
                        handleSearchSuccess={handleSearchSuccess}
                      />
                      <Footer
                        footerLinks={generateFooterLinks({
                          contactUsHandler,
                          isUpgradeFormVariation,
                        })}
                        libVersion={
                          <LibVersion>
                            {import.meta.env.VITE_TARGET_ENV ===
                            'production' ? (
                              <code>{version}</code>
                            ) : (
                              <code>
                                {packageInfo.dependencies['@ltvco/refresh-lib']}
                              </code>
                            )}
                          </LibVersion>
                        }
                        hasMobileApps
                      />
                    </div>
                  </SearchFormProvider>
                </EmailVerification>
              </LibContextWrapper>
            </Growthbook>
          </SubscriptionAccessConfigProvider>
        </SessionProvider>
      </QueryWrapper>
    </ErrorBoundary>
  );
  // END RENDER
}

export default App;
